import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { supabase } from '@/integrations/supabase/client';

export type DashboardLayout = 'score-focused' | 'rate-focused' | 'journey-focused' | 'decision-focused' | 'balanced';

export interface ToolCompletion {
  tool_name: string;
  completion_status: 'not_started' | 'in_progress' | 'completed' | 'needs_update';
  completion_percentage: number;
  last_activity: string;
  completion_data: any;
}

export interface SmartRecommendation {
  id: string;
  recommendation_type: 'next_action' | 'skill_development' | 'tool_suggestion' | 'goal_setting';
  title: string;
  description: string;
  action_url: string;
  priority: number;
  is_dismissed: boolean;
  is_completed: boolean;
}

export interface DashboardPreferences {
  preferred_layout: DashboardLayout;
  last_entry_path: string;
  entry_count: Record<string, number>;
  quick_actions: string[];
  widget_preferences: Record<string, boolean>;
}

interface AdaptiveDashboardContextType {
  preferences: DashboardPreferences | null;
  toolCompletions: ToolCompletion[];
  recommendations: SmartRecommendation[];
  isLoading: boolean;
  updateLayout: (layout: DashboardLayout) => Promise<void>;
  trackEntry: (path: string) => Promise<void>;
  updateToolCompletion: (toolName: string, status: string, percentage: number, data?: any) => Promise<void>;
  dismissRecommendation: (id: string) => Promise<void>;
  completeRecommendation: (id: string) => Promise<void>;
  refreshRecommendations: () => Promise<void>;
}

const AdaptiveDashboardContext = createContext<AdaptiveDashboardContextType | undefined>(undefined);

export const useAdaptiveDashboard = () => {
  const context = useContext(AdaptiveDashboardContext);
  if (!context) {
    throw new Error('useAdaptiveDashboard must be used within an AdaptiveDashboardProvider');
  }
  return context;
};

interface AdaptiveDashboardProviderProps {
  children: ReactNode;
}

export const AdaptiveDashboardProvider = ({ children }: AdaptiveDashboardProviderProps) => {
  const session = useSession();
  const [preferences, setPreferences] = useState<DashboardPreferences | null>(null);
  const [toolCompletions, setToolCompletions] = useState<ToolCompletion[]>([]);
  const [recommendations, setRecommendations] = useState<SmartRecommendation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load user dashboard data
  useEffect(() => {
    if (session?.user?.id) {
      loadDashboardData();
    }
  }, [session?.user?.id]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      const userId = session?.user?.id;
      if (!userId) return;

      // Load preferences
      const { data: prefsData } = await supabase
        .from('user_dashboard_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (prefsData) {
        setPreferences({
          preferred_layout: prefsData.preferred_layout as DashboardLayout,
          last_entry_path: prefsData.last_entry_path || '',
          entry_count: (prefsData.entry_count as Record<string, number>) || {},
          quick_actions: (prefsData.quick_actions as string[]) || [],
          widget_preferences: (prefsData.widget_preferences as Record<string, boolean>) || {},
        });
      }

      // Load tool completions
      const { data: completionsData } = await supabase
        .from('tool_completions')
        .select('*')
        .eq('user_id', userId);

      setToolCompletions((completionsData || []).map(item => ({
        tool_name: item.tool_name,
        completion_status: item.completion_status as 'not_started' | 'in_progress' | 'completed' | 'needs_update',
        completion_percentage: item.completion_percentage,
        last_activity: item.last_activity,
        completion_data: item.completion_data,
      })));

      // Load recommendations
      const { data: recommendationsData } = await supabase
        .from('smart_recommendations')
        .select('*')
        .eq('user_id', userId)
        .eq('is_dismissed', false)
        .eq('is_completed', false)
        .order('priority', { ascending: false });

      setRecommendations((recommendationsData || []).map(item => ({
        id: item.id,
        recommendation_type: item.recommendation_type as 'next_action' | 'skill_development' | 'tool_suggestion' | 'goal_setting',
        title: item.title,
        description: item.description || '',
        action_url: item.action_url || '',
        priority: item.priority,
        is_dismissed: item.is_dismissed,
        is_completed: item.is_completed,
      })));

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateLayout = async (layout: DashboardLayout) => {
    try {
      const userId = session?.user?.id;
      if (!userId) return;

      const { error } = await supabase
        .from('user_dashboard_preferences')
        .upsert({
          user_id: userId,
          preferred_layout: layout,
        });

      if (!error) {
        setPreferences(prev => prev ? { ...prev, preferred_layout: layout } : null);
      }
    } catch (error) {
      console.error('Error updating layout:', error);
    }
  };

  const trackEntry = async (path: string) => {
    try {
      const userId = session?.user?.id;
      if (!userId) return;

      // Call the database function to track entry
      await supabase.rpc('track_dashboard_entry', {
        p_user_id: userId,
        p_entry_path: path
      });

      // Update local state
      setPreferences(prev => {
        if (!prev) return null;
        const newEntryCount = { ...prev.entry_count };
        newEntryCount[path] = (newEntryCount[path] || 0) + 1;
        return { ...prev, last_entry_path: path, entry_count: newEntryCount };
      });

      // Track analytics
      await supabase
        .from('dashboard_analytics')
        .insert({
          user_id: userId,
          action_type: 'dashboard_entry',
          context_data: { entry_path: path },
        });

    } catch (error) {
      console.error('Error tracking entry:', error);
    }
  };

  const updateToolCompletion = async (toolName: string, status: string, percentage: number, data: any = {}) => {
    try {
      const userId = session?.user?.id;
      if (!userId) return;

      const { error } = await supabase
        .from('tool_completions')
        .upsert({
          user_id: userId,
          tool_name: toolName,
          completion_status: status,
          completion_percentage: percentage,
          completion_data: data,
          last_activity: new Date().toISOString(),
        });

      if (!error) {
        setToolCompletions(prev => {
          const existingIndex = prev.findIndex(tc => tc.tool_name === toolName);
          const newCompletion = {
            tool_name: toolName,
            completion_status: status as any,
            completion_percentage: percentage,
            completion_data: data,
            last_activity: new Date().toISOString(),
          };

          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = newCompletion;
            return updated;
          } else {
            return [...prev, newCompletion];
          }
        });

        // Refresh recommendations after tool completion update
        refreshRecommendations();
      }
    } catch (error) {
      console.error('Error updating tool completion:', error);
    }
  };

  const dismissRecommendation = async (id: string) => {
    try {
      const { error } = await supabase
        .from('smart_recommendations')
        .update({ is_dismissed: true })
        .eq('id', id);

      if (!error) {
        setRecommendations(prev => prev.filter(r => r.id !== id));
      }
    } catch (error) {
      console.error('Error dismissing recommendation:', error);
    }
  };

  const completeRecommendation = async (id: string) => {
    try {
      const { error } = await supabase
        .from('smart_recommendations')
        .update({ is_completed: true })
        .eq('id', id);

      if (!error) {
        setRecommendations(prev => prev.filter(r => r.id !== id));
      }
    } catch (error) {
      console.error('Error completing recommendation:', error);
    }
  };

  const refreshRecommendations = async () => {
    try {
      const userId = session?.user?.id;
      if (!userId) return;

      // Generate new recommendations using database function
      const { data: newRecs } = await supabase.rpc('generate_smart_recommendations', {
        p_user_id: userId
      });

      if (newRecs && newRecs.length > 0) {
        // Insert new recommendations
        const { data: insertedRecs } = await supabase
          .from('smart_recommendations')
          .upsert(
            newRecs.map((rec: any) => ({
              user_id: userId,
              recommendation_type: rec.recommendation_type,
              title: rec.title,
              description: rec.description,
              action_url: rec.action_url,
              priority: rec.priority,
            }))
          )
          .select();

        if (insertedRecs) {
          setRecommendations(prev => [...prev, ...insertedRecs.map(item => ({
            id: item.id,
            recommendation_type: item.recommendation_type as 'next_action' | 'skill_development' | 'tool_suggestion' | 'goal_setting',
            title: item.title,
            description: item.description || '',
            action_url: item.action_url || '',
            priority: item.priority,
            is_dismissed: item.is_dismissed,
            is_completed: item.is_completed,
          }))]);
        }
      }
    } catch (error) {
      console.error('Error refreshing recommendations:', error);
    }
  };

  const value: AdaptiveDashboardContextType = {
    preferences,
    toolCompletions,
    recommendations,
    isLoading,
    updateLayout,
    trackEntry,
    updateToolCompletion,
    dismissRecommendation,
    completeRecommendation,
    refreshRecommendations,
  };

  return (
    <AdaptiveDashboardContext.Provider value={value}>
      {children}
    </AdaptiveDashboardContext.Provider>
  );
};